import { useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { ALLOWED_GROUP_OID, graphScopes } from "./authConfig";
import {
  acquireGraphToken,
  fetchJoinedTeams,
  fetchTeamCreatedDates,
  fetchTeamLastActivity,
  isMemberOfGroup,
  Team
} from "./graph";
import TeamSelector from "./components/TeamSelector";
import ExportPanel from "./components/ExportPanel";
import S3Browser from "./components/S3Browser";
import Diagnostics from "./components/Diagnostics";
import { usePersistentState } from "./usePersistentState";
import { setErrorReporterToken } from "./errorReporter";

export default function App() {
  const { instance, accounts } = useMsal();
  const isAuthed = useIsAuthenticated();
  const [graphToken, setGraphToken] = useState<string | null>(null);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = usePersistentState<string[]>(
    "teamsagent.selectedTeamIds",
    []
  );
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const retry = () => setReloadKey((k) => k + 1);

  const login = () =>
    instance.loginRedirect({ scopes: graphScopes }).catch((e) => setErr(String(e)));

  const logout = () => instance.logoutRedirect();

  useEffect(() => {
    if (!isAuthed || accounts.length === 0) return;
    (async () => {
      setLoading(true);
      try {
        const token = await acquireGraphToken(instance, accounts[0]);
        setGraphToken(token);
        setErrorReporterToken(() => token);

        if (ALLOWED_GROUP_OID) {
          const ok = await isMemberOfGroup(token, ALLOWED_GROUP_OID);
          setAllowed(ok);
          if (!ok) {
            setLoading(false);
            return;
          }
        } else {
          setAllowed(true);
        }

        const joined = await fetchJoinedTeams(token);
        setTeams(joined);

        const ids = joined.map((t) => t.id);
        fetchTeamCreatedDates(token, ids).then((m) => {
          setTeams((prev) =>
            prev.map((t) => ({ ...t, createdDateTime: m.get(t.id) ?? t.createdDateTime }))
          );
        });
        fetchTeamLastActivity(token, ids).then((m) => {
          setTeams((prev) =>
            prev.map((t) => ({
              ...t,
              lastActivityDateTime: m.get(t.id) ?? t.lastActivityDateTime
            }))
          );
        });
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthed, accounts, instance, reloadKey]);

  return (
    <div className="app">
      <header className="header">
        <h1>Teams Export &amp; Archive</h1>
        <div>
          {isAuthed ? (
            <>
              <span className="muted" style={{ marginRight: 12 }}>
                {accounts[0]?.username}
              </span>
              <button className="secondary" onClick={logout}>Sign out</button>
            </>
          ) : (
            <button onClick={login}>Sign in</button>
          )}
        </div>
      </header>

      {err && (
        <div className="error">
          {err}{" "}
          <button className="secondary" onClick={retry} style={{ marginLeft: 8 }}>
            Retry
          </button>
        </div>
      )}

      {!isAuthed && (
        <div className="card">
          <p>Sign in with your organizational account to begin.</p>
        </div>
      )}

      {isAuthed && allowed === false && (
        <div className="card error">
          You are not a member of the group authorized to use this app. Contact
          your administrator if you believe this is an error.
        </div>
      )}

      {isAuthed && allowed && (
        <>
          <div className="card">
            <h2>1. Select Teams to export</h2>
            {loading && <div className="loading-bar" />}
            <TeamSelector
              teams={teams}
              selected={selectedTeamIds}
              onChange={setSelectedTeamIds}
            />
          </div>

          <ExportPanel
            graphToken={graphToken}
            teamIds={selectedTeamIds}
            teams={teams}
          />

          <S3Browser graphToken={graphToken} />
          <Diagnostics graphToken={graphToken} />
        </>
      )}
    </div>
  );
}
